import { db } from '../db/index.js';
import { getUnsyncedSessions, markSessionsSynced, claimSessions, markSessionsRejected } from '../db/operations.js';
import {
  apiClient,
  SYNC_MAX_CATEGORY_LENGTH,
  SYNC_MAX_CLIENT_SESSION_ID_LENGTH,
  SYNC_MAX_SESSIONS_PER_REQUEST,
  type SyncSessionPayload,
  type SyncSessionsResponse
} from '../utils/apiClient.js';
import { useAuthStore } from '../stores/authStore.js';
import { isUuid, newId } from '../utils/id.js';
import type { PracticeSession } from '../types/index.js';

const toPayload = (s: PracticeSession): SyncSessionPayload => ({
  client_session_id: s.clientSessionId,
  total_duration_seconds: s.totalDurationSeconds,
  active_playback_seconds: s.activePlaybackSeconds,
  created_at: s.createdAt,
  blocks: s.blocks.map((b) => ({
    ...(isUuid(b.blockId) && { block_id: b.blockId }),
    ...(b.category && { category: String(b.category).slice(0, SYNC_MAX_CATEGORY_LENGTH) }),
    achieved_bpm: b.achievedBpm
  }))
});

async function pushChunked(sessions: PracticeSession[]) {
  const results: SyncSessionsResponse['synced'] = [];
  for (let i = 0; i < sessions.length; i += SYNC_MAX_SESSIONS_PER_REQUEST) {
    const chunk = sessions.slice(i, i + SYNC_MAX_SESSIONS_PER_REQUEST);
    const res = await apiClient.post<SyncSessionsResponse>('/api/sync/sessions', {
      sessions: chunk.map(toPayload)
    });
    results.push(...(res?.synced ?? []));
  }
  return results;
}

/** Replaces a local session's primary key (Dexie key is clientSessionId). */
async function rekeySession(s: PracticeSession): Promise<PracticeSession> {
  const next = { ...s, clientSessionId: newId() };
  await db.transaction('rw', db.sessions, async () => {
    await db.sessions.delete(s.clientSessionId);
    await db.sessions.put(next);
  });
  return next;
}

/**
 * Pushes all unsynced sessions in chunks of <= 200. Items answered with status 'conflict'
 * (id owned by another user) are re-keyed and retried once; if still in conflict they are
 * dropped from the queue (marked synced) so they never retry forever.
 * Returns the number of sessions pushed (0 if nothing was queued).
 */
export async function syncPendingSessions(): Promise<number> {
  const { user, isGuest, token } = useAuthStore.getState();
  const userId = isGuest || !token ? undefined : user?.id;
  if (!userId) return 0; // FRE-108: never upload without a signed-in owner
  await claimSessions(userId);
  const pending = (await getUnsyncedSessions(userId)).filter(
    (s) => s.clientSessionId.length >= 1 && s.clientSessionId.length <= SYNC_MAX_CLIENT_SESSION_ID_LENGTH
  );
  if (pending.length === 0) return 0;

  const results = await pushChunked(pending);
  const conflictIds = new Set(results.filter((r) => r.status === 'conflict').map((r) => r.client_session_id));
  const rejected = results.filter((r) => r.status === 'rejected');
  const rejectedIds = new Set(rejected.map((r) => r.client_session_id));
  const okIds = pending
    .map((s) => s.clientSessionId)
    .filter((id) => !conflictIds.has(id) && !rejectedIds.has(id));
  await markSessionsSynced(okIds);
  if (rejected.length > 0) {
    // Server refused them (VALIDATION / FUTURE_TIMESTAMP): stop retrying, keep the data flagged locally.
    await markSessionsRejected(rejected.map((r) => ({ id: r.client_session_id, reason: r.reason })));
    console.warn(
      `Server rejected ${rejected.length} session(s):`,
      rejected.map((r) => r.reason ?? 'unknown').join(', ')
    );
  }

  if (conflictIds.size > 0) {
    const rekeyed: PracticeSession[] = [];
    for (const s of pending.filter((p) => conflictIds.has(p.clientSessionId))) {
      rekeyed.push(await rekeySession(s));
    }
    const retry = await pushChunked(rekeyed);
    const stillConflict = new Set(retry.filter((r) => r.status === 'conflict').map((r) => r.client_session_id));
    // Synced, or dropped after a second conflict: either way, stop retrying.
    const retryRejected = retry.filter((r) => r.status === 'rejected');
    await markSessionsRejected(retryRejected.map((r) => ({ id: r.client_session_id, reason: r.reason })));
    const retryRejectedIds = new Set(retryRejected.map((r) => r.client_session_id));
    await markSessionsSynced(rekeyed.map((s) => s.clientSessionId).filter((id) => !retryRejectedIds.has(id)));
    if (stillConflict.size > 0) {
      console.warn(`Dropped ${stillConflict.size} session(s) after repeated sync conflict`);
    }
  }
  return pending.length;
}
